import * as Yup from 'yup';

export const getRoutes = (menus) => {
  const routes = [];

  menus.forEach((menu) => {
    if (menu.component && menu.path) {
      routes.push(menu);
    }

    if (menu.children) {
      routes.push(...getRoutes(menu.children));
    }
  });

  return routes;
};

export const buildValidationSchema = (fields) => {
  const schema = {};

  fields.forEach((field) => {
    let validator = Yup.mixed();

    switch (field.type) {
      case 'text':
        validator = Yup.string();
        break;

      case 'checkbox':
        validator = Yup.boolean();
        break;

      case 'select':
        validator = Yup.string().nullable();
        break;

      default:
        validator = Yup.mixed();
    }

    if (field.required) {
      validator = validator.required(`${field.label} is required`);
    }

    schema[field.name] = validator;
  });

  return Yup.object().shape(schema);
};

export const getFirstErrorMessage = (errors) => {
  return Object.values(errors)?.[0]?.message || '';
};

export const isEmpty = (value) => {
  if (value == null) {
    return true;
  }

  if (value instanceof Date) {
    return isNaN(value.getTime());
  }

  if (typeof value === 'string') {
    return value.trim().length === 0;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false;
};

/**
 * Converts an API response into RMSSelect options.
 *
 * @param {Array} data
 * @param {Object} config
 * @param {string} config.labelField - API property to use as label.
 * @param {string} config.valueField - API property to use as value.
 * @param {Array<string>} config.extraFields - Additional properties to keep.
 */
export const createDropdownOptions = (
  data = [],
  { labelField = 'Description', valueField = 'PtsCodeID', extraFields = [], descriptionField } = {}
) => {
  return data.map((item) => {
    const rawLabel = item[labelField];
    const description = descriptionField ? item[descriptionField] : undefined;

    const option = {
      label: description ?? rawLabel,
      value: item[valueField],
      code: rawLabel,
    };

    if (description) {
      option.description = description;
    }

    extraFields.forEach((field) => {
      option[field] = item[field];
    });

    return option;
  });
};

export const parseJsonArray = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) return value;

  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
};

/**
 * Converts buffer data into a Blob object.
 *
 * @param {ArrayBuffer|Uint8Array|Buffer} buffer
 * @param {string} mimeType
 * @returns {Blob}
 */
export const bufferToBlob = (buffer, mimeType = 'application/octet-stream') => {
  return new Blob([buffer], {
    type: mimeType,
  });
};

// Preserve zero and other values; normalize missing values to null.
export const normalizeEmptyToNull = (value) => (value === '' ? null : value ?? null);
